import React, { Dispatch } from "react";
import Cookies from "js-cookie";
import { matchPath } from "react-router-dom";
import moment from "moment";
import { MD5 } from "crypto-js";
import { Collection } from "dexie";
import { db, endpoints, offlineEndpoints } from "configs";
import { generatePath, decryptAES, encryptAES } from "utils";
import {
  AuthData,
  DeferredRequest,
  OfflineModeAssignment,
  User,
  AssignmentId,
} from "types";
import { JsonApiResponse, request } from "@stemscopes/api";
import { register, unregister } from "serviceWorkerRegistration";
import { notification, NewAppVersion } from "components";
import {
  addCachedAssignment,
  addCachedAssignments,
  deleteCachedAssignment,
  setIsOnline,
  setSwStatus,
  setStatus,
  setLoading,
} from "configs/store/offline-mode/actions";
import { fetchComments } from "components/toolbar/components/actions/duck/actions";
import { fetchHighlights } from "components/toolbar/components/actions/components/highlighting/duck/actions";
import { concatAssignments } from "pages/student/assignments/list/duck/selectors";
import {
  fetchFullAssignment,
  fetchFullDistrictAssignment,
  fetchAssignmentsList,
} from "pages/shared/assignment-show/duck/services";
import { generateContent, getAssignmentType } from "./utils";
import {
  FetchStudentAssignment,
  RequestResource,
  FetchDeferred,
} from "./types";
import * as contentOperations from "./contentOperations";

const fetchAssignment = {
  studentAssignment: fetchFullAssignment,
  districtStudentAssignment: fetchFullDistrictAssignment,
};

export const fetchDeferred: FetchDeferred = (
  pin,
  userId,
  callback = () => {},
) => {
  db.deferred
    .where("userId")
    .equals(userId)
    .toArray()
    .then((deferredRequests: DeferredRequest[]) => {
      deferredRequests.forEach((deferredRequest: DeferredRequest) => {
        const path = deferredRequest.id.replace("/isStarted", "");
        (request as any)
          [deferredRequest.method](
            path,
            decryptAES(deferredRequest.data, pin),
            deferredRequest.config || {},
          )
          .catch(() => {})
          .then((response: any) => {
            if (response.status === 200) {
              db.deferred.where("id").equals(deferredRequest.id).delete();
              callback(deferredRequest);
            }
          });
      });
    });
};

export const saveUserToIdb = (data: AuthData): void => {
  db.user
    .where("id")
    .equals(data.user.md5GlobalId)
    .first()
    .then((user: Collection) => {
      if (!user) {
        db.user.add({
          id: data.user.md5GlobalId,
          data: encryptAES(
            {
              ...data,
              authToken: localStorage.getItem("token"),
            },
            data.user.offlineMode.pinCode,
          ),
          pin: MD5(data.user.offlineMode.pinCode).toString(),
        });
      }
    });
  localStorage.setItem("globalId", data.user.md5GlobalId);
  localStorage.setItem("deviceUniqId", data.user.offlineMode.deviceUniqId);
};
export const fetchCachedAssigments = async (
  userId: string,
): Promise<OfflineModeAssignment[]> => {
  const cachedAssignments: OfflineModeAssignment[] = [];
  await db.requestResources
    .where("userId")
    .equals(userId)
    .toArray()
    .then((assignments: any) => {
      assignments.forEach((resources: any) => {
        if (resources.saved) {
          cachedAssignments.push({
            id: resources.id,
            userId: resources.userId,
            title: resources.title,
            content: resources.list || [],
            teacherSectionAssignmentId: resources.teacherSectionAssignmentId,
          });
        }
      });
    });
  return cachedAssignments;
};

export const initOfflineMode = (
  dispatch: React.Dispatch<any>,
  userId: string,
  pin: string,
): void => {
  fetchDeferred(pin, userId);

  window.addEventListener("online", () => {
    dispatch(setIsOnline(true));
    fetchDeferred(pin, userId);
  });
  window.addEventListener("offline", () => {
    dispatch(setIsOnline(false));
  });
  fetchCachedAssigments(userId).then((assignments) =>
    dispatch(addCachedAssignments(assignments)),
  );
};

export const activateServiceWorker = (
  dispatch: React.Dispatch<any>,
  data: AuthData,
): void => {
  const isClient = process.env.REACT_APP_MODE === "client";
  const isInternal = process.env.REACT_APP_MODE === "internal";
  const isNewVersion = Cookies.get("stemscopes-version") === "3.5";

  if ((isClient && isNewVersion) || isInternal) {
    initOfflineMode(
      dispatch,
      data.user.md5GlobalId,
      data.user.offlineMode.pinCode,
    );
    register({
      onUpdate: async (registration) => {
        await registration.unregister();
        notification.createInfo({
          duration: null,
          content: <NewAppVersion registration={registration} />,
        });
      },
      onInstalled: () => {
        saveUserToIdb(data);
        dispatch(setSwStatus("installed"));
      },
    });
  } else if (isClient && !isNewVersion) {
    unregister();
  }
};

export const saveAssignmentToIdb = (response: any, list: string[]): void => {
  const { pathname } = new URL(response.config.url, window.location.origin);

  db.requestResources
    .where("id")
    .equals(pathname)
    .first()
    .then((resources: Collection) => {
      if (!resources) {
        db.requestResources.add({
          id: pathname,
          title: response.data.data.fullName,
          userId: localStorage.getItem("globalId"),
          list,
          saved: true,
          teacherSectionAssignmentId:
            response.data.data.teacherSectionAssignmentId,
        });
      }
    });
};

export const generateAssignmentId = (assignment: any): AssignmentId => {
  return {
    id: generatePath<string>((endpoints as any)[assignment.type], {
      id: assignment.id,
    }),
    needToConvert: assignment.type === "teacherSectionAssignment",
    type: assignment.type,
    districtAssignmentId:
      assignment?.districtAssignment?.id || assignment.districtAssignmentId,
    autoSave: assignment.autoSave || false,
    teacherSectionAssignmentId: assignment?.teacherSectionAssignmentId
      ? generatePath(endpoints.teacherSectionAssignment, {
          id: assignment.teacherSectionAssignmentId,
        })
      : null,
    status: assignment.assignStatus,
  };
};

export const fetchAssignmentsReserve = async (
  user: User,
): Promise<AssignmentId[]> => {
  const { md5GlobalId, id } = user;
  const today = new Date();
  const endDate = moment(today).add(30, "days");
  const assignmentsIds: AssignmentId[] = [];
  let filteredAssignmentsIds: AssignmentId[] = [];

  await fetchAssignmentsList().then((assignmentsList: JsonApiResponse[]) => {
    concatAssignments(
      id,
      assignmentsList[1]?.data?.data,
      assignmentsList[0]?.data?.data,
      assignmentsList[2]?.data?.data,
      [],
      [],
      true,
    ).forEach((assignment: any) => {
      const assignmentId = generateAssignmentId(assignment);

      if (moment(assignment.startOn).isBefore(endDate)) {
        assignmentsIds.push(assignmentId);
      }
    });
  });

  // reserving count of assignments
  request.get(generatePath(endpoints.assignmentsCount));

  if (!localStorage.getItem("assignmentIds")) {
    localStorage.setItem("assignmentIds", JSON.stringify(assignmentsIds));
  }
  const prevAssignmentsList = JSON.parse(
    localStorage.getItem("assignmentIds") || "[]",
  );
  const isNewAssignment = assignmentsIds.find(
    (v1) =>
      !prevAssignmentsList.some(
        (v2: AssignmentId) =>
          v1.id === v2.id || v2.id === v1.teacherSectionAssignmentId,
      ) || v1.status === "Returned",
  );

  await db.requestResources
    .where("userId")
    .equals(md5GlobalId)
    .toArray()
    .then((requestResources: RequestResource[]) => {
      requestResources.reduce(
        (acc: RequestResource[], current: RequestResource) => {
          return [...acc, ...(current.saved ? [current] : [])];
        },
        [],
      );

      if (
        !isNewAssignment &&
        localStorage.getItem("hasDeletedAssignmentsFromCache")
      ) {
        filteredAssignmentsIds = [];
      } else {
        filteredAssignmentsIds = assignmentsIds.filter(
          (assignmentId: AssignmentId) =>
            assignmentId.autoSave &&
            !requestResources.some((v) => v.id.includes(assignmentId.id)),
        );
      }
    });
  localStorage.setItem("assignmentIds", JSON.stringify(assignmentsIds));
  return filteredAssignmentsIds;
};

export const fetchStudentAssignment: FetchStudentAssignment = ({
  ...props
}) => {
  const { assignmentsIds, current, auth, t, dispatch } = props;
  const { id } = matchPath(
    {
      path: offlineEndpoints.find((path) =>
        matchPath(
          {
            path,
          },
          assignmentsIds[current].id,
        ),
      ) as string,
    },
    assignmentsIds[current].id,
  )?.params as { id: string };

  if (!assignmentsIds[current].needToConvert) {
    (fetchAssignment as any)
      [assignmentsIds[current].type]({
        id,
        profileType: auth.data.user.profileType,
        districtAssignmentId: assignmentsIds[current]?.districtAssignmentId,
      })
      .then((assignment: JsonApiResponse[]) => {
        const urlsForCache = generateContent(assignment[0]);
        const mediaUrls: string[] = [];
        const iframeUrls: string[] = [];

        urlsForCache.forEach((url: string) => {
          if (url.indexOf(".html") > 0) {
            iframeUrls.push(url);
          } else if (
            url
              .toLocaleLowerCase()
              .match(/.(?:png|jpe?g|gif|mp4|svg|css|js|pdf)/g) ||
            /https?:\/\/(www\.)?(.*\.)?googleusercontent\.com\/.*/.test(url)
          ) {
            mediaUrls.push(url);
          }
        });

        dispatch(
          setStatus(
            t("offline.savingAssignments", {
              index: current + 1,
              assignmentsLength: assignmentsIds.length,
            }),
          ),
        );

        if (assignmentsIds[current].type === "studentAssignment") {
          dispatch(
            fetchComments.request({
              studentAssignmentId: id,
            }),
          );

          dispatch(
            fetchHighlights.request({
              filter: {
                studentAssignmentId: id,
              },
            }),
          );
        }

        saveAssignmentToIdb(assignment[0], urlsForCache);

        const concatedAssignments = assignment.reduce(
          (acc: { id: string; name: string }[], response) => {
            return [
              ...acc,
              ...(Array.isArray(response.data.data)
                ? response.data.data.reduce((dataAcc, dataItem) => {
                    return [
                      ...dataAcc,
                      { id: dataItem.id, name: dataItem.name },
                    ];
                  }, [])
                : [
                    {
                      id: response.data.data.id,
                      name: response.data.data.name,
                    },
                  ]),
            ];
          },
          [],
        );

        contentOperations
          .fetchContents(mediaUrls)
          .then((fetchedContents: any) => {
            contentOperations.saveContentToCache(
              fetchedContents,
              iframeUrls,
              () => {
                db.requestResources.update(
                  generatePath<string>(
                    (endpoints as any)[assignmentsIds[current].type],
                    {
                      id,
                    },
                  ),
                  {
                    saved: true,
                    userId: auth.data.user.md5GlobalId,
                    type: assignmentsIds[current].type,
                  },
                );
                dispatch(
                  addCachedAssignment({
                    id: assignmentsIds[current].id,
                    userId: auth.data.user.md5GlobalId,
                    title:
                      concatedAssignments.find((item) =>
                        assignmentsIds[current].id.includes(item.id),
                      )?.name || assignment[0].data.data.name,
                    content: urlsForCache || [],
                    teacherSectionAssignmentId:
                      assignmentsIds[current].teacherSectionAssignmentId,
                  }),
                );

                if (assignmentsIds[current + 1] && navigator.onLine) {
                  fetchStudentAssignment({
                    ...props,
                    current: current + 1,
                  });
                } else {
                  dispatch(setLoading(false));
                  dispatch(setStatus(t("offline.saved")));
                }
              },
            );
          });
      });
  } else {
    request
      .get(
        generatePath(endpoints.teacherSectionAssignment, {
          id,
        }),
      )
      .then((response) => {
        if (response.status === 200) {
          assignmentsIds[current] = {
            id: response.data.data.redirectTo,
            needToConvert: false,
            type: "studentAssignment",
            teacherSectionAssignmentId: id,
          };
          fetchStudentAssignment({
            ...props,
            assignmentsIds,
          });
          fetchAssignmentsList();
        }
      });
  }
};

export const clearPWACache = async (
  dispatch: Dispatch<any>,
  userId: string,
): Promise<void> => {
  let requests: RequestResource[] = [];

  await db.requestResources
    .where("userId")
    .equals(userId)
    .toArray()
    .then((result: RequestResource[]) => {
      if (result) {
        requests = result;
      }
    });

  await caches.open("assignments-content").then((cache) => {
    requests.forEach((requestResource: RequestResource) => {
      requestResource.list.forEach((url: string, index: number) => {
        cache.delete(url).then(() => {
          if (requestResource.list.length === index + 1) {
            dispatch(deleteCachedAssignment(requestResource.id));
            db.requestResources.where("id").equals(requestResource.id).delete();
          }
        });
      });
    });
  });
};

export const removeCachedAssignment = (
  assignment: OfflineModeAssignment,
  dispatch: Dispatch<any>,
): void => {
  caches.open("assignments-content").then((cache) => {
    assignment.content.forEach((url: string, index: number) => {
      cache.delete(url).then(() => {});

      if (assignment.content.length === index + 1) {
        dispatch(deleteCachedAssignment(assignment.id));
        db.requestResources.update(
          generatePath<string>(
            (endpoints as any)[
              assignment.type || getAssignmentType(assignment.id)
            ],
            {
              id: assignment.id.replace(/\D/g, ""),
            },
          ),
          { saved: false, userId: null },
        );
      }
    });
  });
};

export const syncCachedAssignments = (
  userId: string,
  dispatch: Dispatch<any>,
): void => {
  const assignmentIds: AssignmentId[] = JSON.parse(
    localStorage.getItem("assignmentIds") || "[]",
  );
  fetchCachedAssigments(userId).then((assignments) => {
    const assignmentsForRemove = assignments.filter(
      (assignment) =>
        !assignmentIds.some((assignmentId) =>
          assignmentId.type === "teacherSectionAssignment"
            ? assignmentId.id.includes(
                assignment.teacherSectionAssignmentId || "",
              )
            : assignmentId.id.includes(assignment.id),
        ),
    );
    assignmentsForRemove.forEach((assignment) =>
      removeCachedAssignment(assignment, dispatch),
    );
  });
};
